// AI usage badge — lives in the topbar, visible to every authenticated role
// including end_user (via the embed widget). KAN-1293.
//
// Display contract (CEO ask: "show credits, not dollars"):
//   • 1 credit = 10 cents (defined in CENTS_PER_CREDIT). Backend stays in
//     cents — this is purely a render-layer rebrand.
//   • Badge shows REMAINING credits, not used. Number counts DOWN as the
//     user spends. "49 / 50 credits" → "48 / 50" → ... → "0 / 50".
//   • Floor at 0 — never display a negative remaining. If usage > limit
//     (which can happen for a single call that exceeds the cap mid-flight),
//     show "0 / 50 credits" and let enforcement block the next request.
//   • The trailing % shows % USED (so danger states match enforcement
//     intuition: high % = bad). It reads inline after a middot rather than in
//     its own filled chip — see `.credits` in styles.css.
//
// Real data comes from useAIUsage; the slide-over is rendered as a sibling
// because it overlays the page and shouldn't inherit the topbar's z-index.

import { useEffect } from "react";
import { capture } from "@/lib/posthog";
import * as React from "react";
import { Icon } from "@/pages/prototype-icons";
import { useAIUsage, type AIUsageResponse } from "@/hooks/use-ai-usage";
import { cn } from "@/lib/utils";
import { centsToCredits, remainingCredits } from "@/lib/credits";
import { AIUsageSlideOver } from "./AIUsageSlideOver";

function fmtCredits(n: number): string {
  return n.toLocaleString();
}

function pct(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

interface PresentationProps {
  data: AIUsageResponse;
  onClick: () => void;
}

/**
 * Pure presentation component — accepts pre-fetched data so it can be reused
 * by the embed without re-wiring the auth context. Exported for the storybook
 * preview / future tests; the default export (`AIUsageBadge`) wraps the hook.
 */
export function AIUsageBadgePresentation({ data, onClick }: PresentationProps): React.ReactElement {
  const unlimited = data.limitCents < 0;
  const usedPct = unlimited ? 0 : pct(data.usedCents, data.limitCents);

  // Color escalation tracks % USED (high = bad), mirrors enforcement.
  const danger  = !unlimited && usedPct >= 90;
  const warning = !unlimited && usedPct >= 75 && usedPct < 90;

  // The topbar already renders a danger state at 90% used — that IS an upgrade
  // prompt, it just was not counted as one. Without this, the monetization
  // funnel has no denominator: you can see who requested an upgrade but not how
  // many people were shown a wall and did nothing.
  //
  // AI spend is the one quota dimension that costs fastn real money per unit,
  // so the gap between "warned" and "blocked" matters more here than anywhere
  // else. Fires once per danger transition, not per render.
  useEffect(() => {
    if (!danger) return;
    capture("upgrade_prompt_shown", {
      trigger: "usage_badge",
      dimension: "llm_cost_cents_per_month",
      plan: null,
    });
  }, [danger]);

  const totalCredits     = unlimited ? 0 : centsToCredits(data.limitCents);
  const remainingCreditsValue = unlimited ? 0 : remainingCredits(data.usedCents, data.limitCents);
  const usedCreditsValue = centsToCredits(data.usedCents);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="ai-usage-badge"
      aria-label={
        unlimited
          ? `AI credits: ${usedCreditsValue} used of unlimited (Enterprise plan)`
          : `AI credits: ${remainingCreditsValue} of ${totalCredits} remaining this month`
      }
      // GL3: reuse the clear explanation already written for the slide-over
      // panel — "unclear what a credit is, what uses them" was a copy gap,
      // not something that needed new content invented.
      title="Each model call uses credits — workflow builds, intelligence briefs, and the browser agent all draw from the same monthly pool. Click for details."
      className={cn("credits", "transition-colors hover:bg-accent",
        danger ? "credits--danger" : warning ? "credits--warn" : null,
      )}
    >
      <Icon name="zap" />
      <span className="tabular-nums">
        {unlimited ? (
          <>
            <b>{fmtCredits(usedCreditsValue)}</b> / <span aria-label="infinity">∞</span> credits
          </>
        ) : (
          <>
            {/* GL3: "35 / 50 credits" reads as either used or left. The number
                counts DOWN, so say so on the badge itself rather than only in
                the tooltip and the aria-label. */}
            <b>{fmtCredits(remainingCreditsValue)}</b>
            <span className="mx-1 text-muted-foreground">of</span>
            <span className="text-muted-foreground">{fmtCredits(totalCredits)} AI credits left</span>
          </>
        )}
      </span>
    </button>
  );
}

/**
 * Hook-wired version — used in the dashboard topbar. Hidden until the first
 * fetch completes (no skeleton — a flicker in the chrome is worse than a
 * brief gap). Errors silently hide the badge; we never want a topbar to
 * render in an error state for what's effectively a nice-to-have widget.
 */
export function AIUsageBadge({ showUpgradeCTA = true }: { showUpgradeCTA?: boolean } = {}): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  // While the slide-over is open it polls this key at 5s itself — pause the
  // badge's own timer so the shared cache has exactly one poller.
  const { data, isLoading, isError } = useAIUsage("org", { poll: !open });

  if (isLoading || isError || !data) return null;

  return (
    <>
      <AIUsageBadgePresentation data={data} onClick={() => setOpen(true)} />
      {open && (
        <AIUsageSlideOver onClose={() => setOpen(false)} showUpgradeCTA={showUpgradeCTA} />
      )}
    </>
  );
}
